import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { agentApi } from "../services/agentApi";
import AgendaWeekGrid, { startOfWeek } from "../components/agent/AgendaWeekGrid";
import AppointmentDrawer from "../components/agent/AppointmentDrawer";
import WeeklyRulesModal from "../components/agent/WeeklyRulesModal";
import UpcomingAppointmentsList from "../components/agent/UpcomingAppointmentsList";

function addDays(d, n) {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

export default function AgentTraderAgenda() {
  const [params, setParams] = useSearchParams();
  const [traders, setTraders] = useState([]);
  const [traderId, setTraderId] = useState(null);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showRules, setShowRules] = useState(false);
  const [drawerAppointmentId, setDrawerAppointmentId] = useState(params.get("appointment") || null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    agentApi.traders.list()
      .then((r) => {
        const list = r.traders || [];
        setTraders(list);
        if (!traderId && list[0]) setTraderId(list[0].id);
      })
      .catch((err) => setError(err.message));
  }, []);

  useEffect(() => {
    if (!traderId) return;
    setLoading(true);
    const from = weekStart.toISOString();
    const to = addDays(weekStart, 7).toISOString();
    agentApi.traders.listSlots(traderId, { from, to })
      .then((r) => setSlots(r.slots || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [traderId, weekStart, refreshKey]);

  useEffect(() => {
    if (drawerAppointmentId) setParams({ appointment: drawerAppointmentId });
    else setParams({});
  }, [drawerAppointmentId]);

  async function handleSlotClick(slot) {
    if (slot.status === "BOOKED" && slot.appointment) {
      setDrawerAppointmentId(slot.appointment.id);
      return;
    }
    if (slot.status === "FREE") {
      if (!confirm("Bloquear este horário?")) return;
      try {
        await agentApi.traders.blockSlot(traderId, slot.id);
        setRefreshKey((k) => k + 1);
      } catch (err) { alert(`Erro: ${err.message}`); }
      return;
    }
    if (slot.status === "BLOCKED") {
      if (!confirm("Liberar este horário?")) return;
      try {
        await agentApi.traders.unblockSlot(traderId, slot.id);
        setRefreshKey((k) => k + 1);
      } catch (err) { alert(`Erro: ${err.message}`); }
    }
  }

  async function handleGenerate() {
    setGenerating(true);
    try {
      const r = await agentApi.traders.generateSlots(traderId, 4);
      alert(`${r.created} novos slots gerados.`);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      if ((err.message || "").includes("weeklyRules")) {
        alert("Configure as regras semanais primeiro.");
      } else {
        alert(`Erro: ${err.message}`);
      }
    } finally {
      setGenerating(false);
    }
  }

  const trader = traders.find((t) => t.id === traderId);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-semibold text-gray-900">Agenda</h1>
          {traders.length > 0 && (
            <select
              value={traderId || ""}
              onChange={(e) => setTraderId(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-300 text-gray-900 rounded text-sm"
            >
              {traders.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.user?.name || t.user?.email || t.id}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowRules(true)}
            className="px-3 py-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded text-sm"
          >
            ⚙ Regras semanais
          </button>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white rounded text-sm font-medium"
          >
            {generating ? "Gerando..." : "+ Gerar 4 semanas"}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">{error}</div>
      )}

      {!trader ? (
        <div className="bg-white rounded-lg p-8 text-center border border-gray-200">
          <p className="text-gray-700">
            {traders.length === 0
              ? "Nenhum trader cadastrado. Solicite ao admin."
              : "Carregando..."}
          </p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg p-4 border border-gray-200 mb-6">
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => setWeekStart(addDays(weekStart, -7))}
                      className="px-3 py-1 text-gray-700 hover:bg-gray-100 rounded">
                ◀ Semana anterior
              </button>
              <button onClick={() => setWeekStart(startOfWeek(new Date()))}
                      className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded font-medium">
                Hoje
              </button>
              <button onClick={() => setWeekStart(addDays(weekStart, 7))}
                      className="px-3 py-1 text-gray-700 hover:bg-gray-100 rounded">
                Próxima semana ▶
              </button>
            </div>
            {loading ? (
              <div className="text-gray-500 text-sm">Carregando slots...</div>
            ) : (
              <AgendaWeekGrid slots={slots} weekStart={weekStart} onSlotClick={handleSlotClick} />
            )}
          </div>

          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Próximas reuniões</h2>
            <UpcomingAppointmentsList
              traderId={traderId}
              refreshKey={refreshKey}
              onClick={setDrawerAppointmentId}
            />
          </div>
        </>
      )}

      {showRules && (
        <WeeklyRulesModal
          traderId={traderId}
          onClose={() => setShowRules(false)}
          onSaved={() => setRefreshKey((k) => k + 1)}
        />
      )}

      {drawerAppointmentId && (
        <AppointmentDrawer
          appointmentId={drawerAppointmentId}
          onClose={() => setDrawerAppointmentId(null)}
          onCancelled={() => setRefreshKey((k) => k + 1)}
        />
      )}
    </div>
  );
}
